/**
 * Audio Timing Editor Component
 * JSON editor for Veo 3 audio timing configuration
 * Validates: Requirements 17.2
 */
import { useState, useEffect } from 'react';
import { useVideoStore, AudioTimingSegment } from '../../stores/videoStore';

interface AudioTimingEditorProps {
  disabled?: boolean;
}

const EXAMPLE_AUDIO_TIMING: AudioTimingSegment[] = [
  {
    startTime: 0,
    endTime: 3,
    text: "Welcome to our video",
    voiceStyle: "natural"
  },
  {
    startTime: 3,
    endTime: 7,
    text: "Here's what we'll cover today",
    voiceStyle: "professional"
  },
  {
    startTime: 7,
    endTime: 10,
    text: "Let's get started!",
    voiceStyle: "enthusiastic"
  }
];

export function AudioTimingEditor({ disabled }: AudioTimingEditorProps) {
  const {
    selectedModel,
    audioTimingJson,
    setAudioTimingJson,
    settings,
  } = useVideoStore();

  const [isExpanded, setIsExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Only show for Veo 3
  const isVeo3 = selectedModel === 'veo-3';

  // Validate JSON on change
  useEffect(() => {
    if (!audioTimingJson.trim()) {
      setValidationError(null);
      return;
    }

    try {
      const parsed = JSON.parse(audioTimingJson) as AudioTimingSegment[];
      
      if (!Array.isArray(parsed)) {
        setValidationError('Audio timing must be an array');
        return;
      }

      const duration = settings.duration || 10;

      for (let i = 0; i < parsed.length; i++) {
        const segment = parsed[i]!;
        
        if (typeof segment.startTime !== 'number' || typeof segment.endTime !== 'number') {
          setValidationError(`Segment ${i + 1}: startTime and endTime must be numbers`);
          return;
        }

        if (!segment.text || typeof segment.text !== 'string') {
          setValidationError(`Segment ${i + 1}: text is required`);
          return;
        }

        if (segment.startTime < 0) {
          setValidationError(`Segment ${i + 1}: startTime cannot be negative`);
          return;
        }

        if (segment.endTime <= segment.startTime) {
          setValidationError(`Segment ${i + 1}: endTime must be greater than startTime`);
          return;
        }

        if (segment.endTime > duration) {
          setValidationError(`Segment ${i + 1}: endTime (${segment.endTime}s) exceeds video duration (${duration}s)`);
          return;
        }

        // Check for overlapping segments
        if (i > 0) {
          const prevSegment = parsed[i - 1]!;
          if (segment.startTime < prevSegment.endTime) {
            setValidationError(`Segments ${i} and ${i + 1} overlap`);
            return;
          }
        }
      }

      setValidationError(null);
    } catch {
      setValidationError('Invalid JSON format');
    }
  }, [audioTimingJson, settings.duration]);

  const handleLoadExample = () => {
    setAudioTimingJson(JSON.stringify(EXAMPLE_AUDIO_TIMING, null, 2));
  };

  const handleClear = () => {
    setAudioTimingJson('');
  };

  if (!isVeo3) {
    return null;
  }

  return (
    <div className="bg-gray-800/50 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-700/50 transition-colors"
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-sm font-medium text-gray-300">Audio Timing (Veo 3)</span>
          {audioTimingJson.trim() && (
            <span className={`text-xs px-2 py-0.5 rounded ${validationError ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'}`}>
              {validationError ? 'Invalid' : 'Valid'}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">Optional</span>
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-3">
          <p className="text-xs text-gray-400">
            Configure audio timing segments for synchronized narration. Each segment defines when text should be spoken.
          </p>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleLoadExample}
              disabled={disabled}
              className="text-xs text-blue-400 hover:text-blue-300 disabled:opacity-50"
            >
              Load Example
            </button>
            <span className="text-gray-600">|</span>
            <button
              onClick={handleClear}
              disabled={disabled || !audioTimingJson}
              className="text-xs text-gray-400 hover:text-gray-300 disabled:opacity-50"
            >
              Clear
            </button>
          </div>

          {/* JSON Editor */}
          <textarea
            value={audioTimingJson}
            onChange={(e) => setAudioTimingJson(e.target.value)}
            disabled={disabled}
            placeholder={`[\n  {\n    "startTime": 0,\n    "endTime": 3,\n    "text": "Your narration text",\n    "voiceStyle": "natural"\n  }\n]`}
            className="w-full h-48 bg-gray-900 text-gray-100 text-sm font-mono rounded-lg p-3 border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50"
          />

          {/* Validation Error */}
          {validationError && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {validationError}
            </p>
          )}

          {/* Schema Reference */}
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-400">Schema Reference</summary>
            <pre className="mt-2 p-2 bg-gray-900 rounded text-gray-400 overflow-x-auto">
{`{
  "startTime": number,  // Start time in seconds
  "endTime": number,    // End time in seconds
  "text": string,       // Narration text
  "voiceStyle": string  // Optional: "natural", "professional", "enthusiastic"
}`}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
